import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Modal } from 'ncoded-component-library';
import { User } from 'models/User';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import { AxiosResponse } from 'axios';

import './AddUserModal.styles.scss';
import './AddUserModal.styles.responsive.scss';

type AddUserModalProps = {
  className?: string;
  addUserRequest: (values: Partial<User>) => Promise<AxiosResponse<User>>;
  withFleetName?: boolean;
  onSuccess?: React.Dispatch<React.SetStateAction<User[]>>;
};

const AddUserModal: ForwardRefRenderFunction<OverlayRef, AddUserModalProps> = (
  props,
  ref,
) => {
  const { className, addUserRequest, withFleetName, onSuccess } = props;
  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const { t } = useTranslation();
  const { popError, popSuccess } = useContext(PopNotificationsContext);

  const classes = classNames('add-user-modal', className);

  useImperativeHandle(ref, () => modal, [modal]);

  const onAddUser = useCallback(
    async (values: Partial<User>) => {
      try {
        const { data } = await addUserRequest(values);

        popSuccess(t('userAdded'));
        modal?.close();
        data && onSuccess?.((old) => [...old, data]);
      } catch (e) {
        popError(e);
      }
    },
    [addUserRequest, modal, onSuccess, popError, popSuccess, t],
  );

  return (
    <Modal ref={modalRef} className={classes}>
      <Form
        onSubmit={onAddUser}
        render={({ handleSubmit, submitting, pristine }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('addUser')}</p>
            <Field
              label={t('firstName')}
              name="firstName"
              component={InputField}
              required
            />
            <Field
              label={t('lastName')}
              name="lastName"
              component={InputField}
              required
            />
            {withFleetName && (
              <Field
                label={t('fleetName')}
                name="fleetName"
                component={InputField}
                required
              />
            )}
            <Field
              label={t('email')}
              name="email"
              component={InputField}
              required
            />

            <div className="actions">
              <Button aria-label="close" variant="link" onClick={modal.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={pristine || submitting}>
                {t('submit')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AddUserModal);
